import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardSummary } from '../../api/dashboardApi';
import { getErrorMessage } from '../../api/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { getRoleLabel } from '../../auth/roles';
import type { DashboardSummary } from '../../types/dashboard';
import './Dashboard.css';

function formatStatus(status: string | null | undefined): string {
  return String(status || 'Unknown').replaceAll('_', ' ');
}

function statusClass(status: string | null | undefined): string {
  return String(status || 'unknown').toLowerCase().replaceAll('_', '-');
}

function DashboardOverview() {
  const { currentUser } = useAuth();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    getDashboardSummary()
      .then((response) => {
        if (active) {
          setSummary(response);
        }
      })
      .catch((requestError: unknown) => {
        if (active) {
          setError(getErrorMessage(requestError, 'The dashboard could not be loaded.'));
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <section className="dashboard-page">
        <div className="dashboard-empty-state" role="alert">
          <h1>Dashboard unavailable</h1>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  if (!summary || !currentUser) {
    return <div className="dashboard-route-status" role="status">Loading dashboard...</div>;
  }

  const { metrics, recentProjects = [] } = summary;

  return (
    <section className="dashboard-page">
      <div className="dashboard-page-header">
        <div>
          <p className="eyebrow">{getRoleLabel(currentUser.role)} workspace</p>
          <h1>Project tracking</h1>
        </div>
        <Link className="dashboard-action" to="/dashboard/projects">View projects</Link>
      </div>

      <div className="metric-grid">
        <article className="metric-card">
          <span>Visible projects</span>
          <strong>{metrics.projectCount}</strong>
        </article>
        <article className="metric-card">
          <span>Needs attention</span>
          <strong>{metrics.attentionCount}</strong>
        </article>
        <article className="metric-card">
          <span>Open milestones</span>
          <strong>{metrics.openMilestoneCount}</strong>
        </article>
        <article className="metric-card">
          <span>Next milestone</span>
          <strong>{metrics.nextMilestone?.title || 'None'}</strong>
        </article>
      </div>

      <div className="dashboard-section">
        <h2>Active work</h2>
        <div className="project-card-grid">
          {recentProjects.map((project) => (
            <Link key={project.id} className="dashboard-project-card" to={`/dashboard/projects/${project.id}`}>
              <div className="project-card-topline">
                <span className={`status-pill status-${statusClass(project.health)}`}>
                  {formatStatus(project.health)}
                </span>
                <span>{project.priority}</span>
              </div>
              <h3>{project.name}</h3>
              <p>{project.summary}</p>
              <div className="project-meta-row">
                <span>{project.phase}</span>
                <span>Due {project.targetDate}</span>
              </div>
            </Link>
          ))}
          {recentProjects.length === 0 && (
            <p className="inline-empty-state">No projects are assigned to this account.</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default DashboardOverview;
